import { captureException, withScope } from "@sentry/react";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import type {
	RenderSource,
	RenderRunner,
	LoadedScene,
} from "./render-runner-type.ts";
import type {
	RenderWorkerIncomingData,
	RenderWorkerOutgoingData,
} from "./render-worker-data.ts";
import { deserializeError } from "../utils/worker-error.ts";
import type { FaceBoundingBox } from "@brickme/project-core/src/model/project.ts";

// Interesting observation, main thread rendering was faster in one test i did. 2.5ms
// compared to 7-14. Pretty small example though. I'm keeping in worker for better ui
// responsiveness. Test again when have a larger project

function createRenderWorkerBridge(): RenderRunner<HTMLCanvasElement> {
	// Note: neither format of worker works locally for me atm. Either in a built preview or
	// in dev mode. Haven't tried an isolated hello world. It's working fine on dev though.
	const renderWorker = new Worker(
		new URL("./render-worker.ts", import.meta.url),
		{
			type: "module",
		},
	);

	// The render worker runs Canvas 2D with `alpha: false`, so a swallowed render
	// failure shows on screen as a blank/black canvas with nothing logged (Sentry
	// only runs on the main thread). Surface anything the worker forwards.
	renderWorker.onmessage = (
		e: MessageEvent<RenderWorkerOutgoingData>,
	): void => {
		if (e.data.type === "render-error") {
			const error = deserializeError(e.data.error);
			// eslint-disable-next-line no-console
			console.error(`Render worker error (${e.data.context})`, error);
			withScope((scope) => {
				scope.setTag("source", "render-worker");
				scope.setExtra("renderWorkerErrorContext", e.data.context);
				captureException(error);
			});
		}
	};
	// Backstop for an uncaught worker error the worker code didn't forward itself.
	renderWorker.onerror = (event: ErrorEvent): void => {
		// eslint-disable-next-line no-console
		console.error("Render worker uncaught error", event.message);
		withScope((scope) => {
			scope.setTag("source", "render-worker");
			scope.setExtra("renderWorkerErrorContext", "worker-uncaught");
			captureException(
				event.error ?? new Error(event.message || "Render worker error"),
			);
		});
	};

	return {
		setSourceImage(sourceImage: Bitmap, utilityCanvas: HTMLCanvasElement) {
			const offscreenCanvas = utilityCanvas.transferControlToOffscreen();
			renderWorker.postMessage(
				{
					type: "set-source-image",
					utilityCanvas: offscreenCanvas,
					sourceImage,
				} satisfies RenderWorkerIncomingData,
				[offscreenCanvas],
			);
		},
		setPictureConfiguration(picture: PictureConfiguration) {
			renderWorker.postMessage(
				{
					type: "set-picture",
					picture,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setCanvas(canvas: HTMLCanvasElement) {
			const offscreenCanvas = canvas.transferControlToOffscreen();
			renderWorker.postMessage(
				{
					type: "set-canvas",
					canvas: offscreenCanvas,
				} satisfies RenderWorkerIncomingData,
				[offscreenCanvas],
			);
		},
		setSource(newSource: RenderSource | undefined) {
			renderWorker.postMessage(
				{
					type: "set-source",
					source: newSource,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setFaces(
			newFaces:
				| readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
				| undefined,
		) {
			renderWorker.postMessage(
				{
					type: "set-faces",
					faces: newFaces,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setDrawBaseplates(newDrawBaseplates: boolean) {
			renderWorker.postMessage(
				{
					type: "set-draw-baseplates",
					drawBaseplates: newDrawBaseplates,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setDrawDebugFaces(newDrawDebugFaces: boolean) {
			renderWorker.postMessage(
				{
					type: "set-draw-debug-faces",
					drawDebugFaces: newDrawDebugFaces,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setLoadedScene(loadedScene: LoadedScene | undefined) {
			renderWorker.postMessage(
				{
					type: "set-loaded-scene",
					loadedScene,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setUtilityCanvas(
			newUtilityCanvas: HTMLCanvasElement,
			canvasSize: GenericSize<number>,
		) {
			const utilityCanvas = newUtilityCanvas.transferControlToOffscreen();
			renderWorker.postMessage(
				{
					type: "set-utility-canvas",
					utilityCanvas,
					canvasSize,
				} satisfies RenderWorkerIncomingData,
				[utilityCanvas],
			);
		},
		clearUtilityCanvas() {
			renderWorker.postMessage(
				{
					type: "clear-utility-canvas",
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		terminate() {
			renderWorker.terminate();
		},
	};
}

export { createRenderWorkerBridge };
